import React, { useEffect } from 'react';

import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useOrderContext } from 'state/order';
import { useServiceModeContext } from 'state/service-mode';
import { ServiceMode } from 'state/service-mode/types';
import { useStoreContext } from 'state/store';
import { VariationFlags } from 'utils/launchdarkly';

import { PickupServiceModeOption } from './pickup-service-mode-option';
import { serviceModeList } from './service-mode-list';
import { Content } from './styled';

const Container = styled.div`
  border-top: 1px solid ${Styles.color.grey.four};
  background-color: ${Styles.color.cardBackground};
  border-bottom-right-radius: ${Styles.borderRadius};
  border-bottom-left-radius: ${Styles.borderRadius};
`;

export const ServiceModeDetails = () => {
  const { serviceMode: selectedServiceMode } = useOrderContext();
  const { serviceModeStatus } = useStoreContext();
  const { setServiceMode } = useServiceModeContext();
  const { formatMessage } = useIntl();

  const hideServiceModeOption =
    useFlag<VariationFlags>(LaunchDarklyFlag.HIDE_SERVICE_MODE_OPTION) || {};

  /*
   * This will validate the currently selected service modal is still available otherwise
   * default to the first available service mode
   */
  useEffect(() => {
    if (
      serviceModeStatus[selectedServiceMode].available &&
      !serviceModeStatus[selectedServiceMode].disabled
    ) {
      return;
    }

    const firstAvailableServiceMode = serviceModeList.find(serviceMode => {
      return serviceModeStatus[serviceMode].available && !serviceModeStatus[serviceMode].disabled;
    });

    if (firstAvailableServiceMode) {
      setServiceMode(firstAvailableServiceMode);
    }
  }, [selectedServiceMode, serviceModeStatus, setServiceMode]);

  const ServiceModeComponents = {
    [ServiceMode.TAKEOUT]: (
      <PickupServiceModeOption
        key={ServiceMode.TAKEOUT}
        serviceMode={ServiceMode.TAKEOUT}
        header={formatMessage({ id: 'serviceModeDetailsPickupHeader' })}
        description={formatMessage({ id: 'serviceModeDetailsPickupDescription' })}
        unavailable={serviceModeStatus.TAKEOUT.disabled}
        hidden={!serviceModeStatus.TAKEOUT.available || hideServiceModeOption[ServiceMode.TAKEOUT]}
      />
    ),
    [ServiceMode.CURBSIDE]: (
      <PickupServiceModeOption
        key={ServiceMode.CURBSIDE}
        serviceMode={ServiceMode.CURBSIDE}
        header={formatMessage({ id: 'serviceModeDetailsCurbsideHeader' })}
        description={formatMessage({ id: 'serviceModeDetailsCurbsideDescription' })}
        unavailable={serviceModeStatus.CURBSIDE.disabled}
        hidden={
          !serviceModeStatus.CURBSIDE.available || hideServiceModeOption[ServiceMode.CURBSIDE]
        }
      />
    ),
    [ServiceMode.DRIVE_THRU]: (
      <PickupServiceModeOption
        key={ServiceMode.DRIVE_THRU}
        serviceMode={ServiceMode.DRIVE_THRU}
        header={formatMessage({ id: 'serviceModeDetailsDriveThruHeader' })}
        description={formatMessage({ id: 'serviceModeDetailsDriveThruDescription' })}
        unavailable={serviceModeStatus.DRIVE_THRU.disabled}
        hidden={
          !serviceModeStatus.DRIVE_THRU.available || hideServiceModeOption[ServiceMode.DRIVE_THRU]
        }
      />
    ),
    [ServiceMode.EAT_IN]: (
      <PickupServiceModeOption
        key={ServiceMode.EAT_IN}
        serviceMode={ServiceMode.EAT_IN}
        header={formatMessage({ id: 'serviceModeDetailsDineInHeader' })}
        description={formatMessage({ id: 'serviceModeDetailsDineInDescription' })}
        unavailable={serviceModeStatus.EAT_IN.disabled}
        hidden={!serviceModeStatus.EAT_IN.available || hideServiceModeOption[ServiceMode.EAT_IN]}
      />
    ),
    [ServiceMode.TABLE_SERVICE]: (
      <PickupServiceModeOption
        key={ServiceMode.TABLE_SERVICE}
        serviceMode={ServiceMode.TABLE_SERVICE}
        header={formatMessage({ id: 'serviceModeDetailsTableHeader' })}
        description={formatMessage({ id: 'serviceModeDetailsTableDescription' })}
        unavailable={serviceModeStatus.TABLE_SERVICE.disabled}
        hidden={
          !serviceModeStatus.TABLE_SERVICE.available ||
          hideServiceModeOption[ServiceMode.TABLE_SERVICE]
        }
      />
    ),
  };

  return (
    <Container>
      <Content>{serviceModeList.map(key => ServiceModeComponents[key] || null)}</Content>
    </Container>
  );
};
